/**
 * The typed blocks a Doc's card shows, read from its markdown (V4 content,
 * its card, a legacy note or a markdown frame), so the card is a small
 * version of the document. Pure: no BlockNote, no editor.
 */
import { markdownToPlainText } from "@/lib/markdown-plain-text";
import { docMarkdown, isLegacyNoteContent } from "./doc-content";

export type DocCardLineKind =
  | "heading"
  | "text"
  | "check"
  | "bullet"
  | "quote"
  | "code";

export interface DocCardLine {
  kind: DocCardLineKind;
  /** Paragraphs, quotes and code keep their hard line breaks as `\n`. */
  text: string;
  /** Check lines only. */
  done?: boolean;
  /** Heading lines only: 1–6. */
  level?: number;
  /** The link labels in `text`, in order; absent when it has none. */
  links?: DocCardLink[];
}

/** A link's label in a line's `text`: UTF-16 offsets, `end` exclusive. */
export interface DocCardLink {
  start: number;
  end: number;
}

type InlineText = Pick<DocCardLine, "text" | "links">;

/** Most blocks a card reads; the card clips what does not fit its height. */
export const DOC_CARD_MAX_LINES = 40;
/** Markdown read before parsing, so a huge document stays cheap. */
const SOURCE_MAX_CHARS = 4000;

const FENCE = /^\s*(```|~~~)/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
const CHECK = /^\s*(?:[-*+]\s+)?\[([ xX])\]\s+(.*)$/;
const BULLET = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/;
const QUOTE = /^\s*>\s?(.*)$/;
const TABLE_ROW = /^\s*\|(.*)\|\s*$/;
const TABLE_RULE = /^\s*\|?[\s:|-]*-{3,}[\s:|-]*$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
/** Two trailing spaces or a backslash end a line with a hard break. */
const HARD_BREAK = /(?: {2,}|\\)$/;

const IMAGE = /!\[[^\]]*\]\([^)]*\)/g;
/** An unescaped `[label](url)`, as `markdownToPlainText` strips it. */
const LINK = /(?<!\\)\[([^\]]+)\]\((?:[^()]|\([^()]*\))+\)/g;
/** Private-use marks around a link label while the markup is stripped. */
const LINK_OPEN = "\uE100";
const LINK_CLOSE = "\uE101";
/** The marks, dropped from the source so its own text is never read as one. */
const LINK_MARKS = /[\uE100\uE101]/g;

/** One line of markdown as plain text, its whitespace collapsed, links kept. */
function inline(markdown: string): InlineText {
  const marked = markdownToPlainText(
    markdown
      .replace(LINK_MARKS, "")
      .replace(IMAGE, " ")
      .replace(LINK, `${LINK_OPEN}$1${LINK_CLOSE}`),
  );
  const links: DocCardLink[] = [];
  let text = "";
  // A label starts at its first visible character; -1 until one is seen.
  let start: number | null = null;
  for (const char of marked) {
    if (char === LINK_OPEN) {
      start = -1;
    } else if (char === LINK_CLOSE) {
      if (start !== null && start >= 0) {
        links.push({ start, end: text.trimEnd().length });
      }
      start = null;
    } else if (!/\s/.test(char)) {
      if (start === -1) start = text.length;
      text += char;
    } else if (text && !text.endsWith(" ")) {
      text += " ";
    }
  }
  text = text.trimEnd();
  return links.length > 0 ? { text, links } : { text };
}

/** `parts` joined by `separator`, their links moved with them. */
function joinInline(
  parts: readonly InlineText[],
  separator: string,
): InlineText {
  const links: DocCardLink[] = [];
  let text = "";
  parts.forEach((part, index) => {
    if (index > 0) text += separator;
    for (const link of part.links ?? []) {
      links.push({
        start: link.start + text.length,
        end: link.end + text.length,
      });
    }
    text += part.text;
  });
  return links.length > 0 ? { text, links } : { text };
}

function tableText(row: string): InlineText {
  const cells = row
    .split("|")
    .map(inline)
    .filter((cell) => cell.text);
  return joinInline(cells, " · ");
}

function lineFor(raw: string): DocCardLine | null {
  if (RULE.test(raw) || TABLE_RULE.test(raw)) return null;
  const heading = HEADING.exec(raw);
  if (heading) {
    return {
      kind: "heading",
      ...inline(heading[2] ?? ""),
      level: heading[1]?.length ?? 1,
    };
  }
  const check = CHECK.exec(raw);
  if (check) {
    return {
      kind: "check",
      ...inline(check[2] ?? ""),
      done: check[1] !== " ",
    };
  }
  const bullet = BULLET.exec(raw);
  if (bullet) return { kind: "bullet", ...inline(bullet[1] ?? "") };
  const quote = QUOTE.exec(raw);
  if (quote) return { kind: "quote", ...inline(quote[1] ?? "") };
  const table = TABLE_ROW.exec(raw);
  if (table) return { kind: "text", ...tableText(table[1] ?? "") };
  return { kind: "text", ...inline(raw) };
}

/**
 * Whether `line` continues the `open` block, as markdown reads it: a plain
 * line continues a paragraph or a list item, a `>` line continues a quote.
 */
function continues(
  open: DocCardLine | null,
  line: DocCardLine,
  raw: string,
): open is DocCardLine {
  if (!open) return false;
  if (line.kind === "quote") return open.kind === "quote";
  return line.kind === "text" && !TABLE_ROW.test(raw);
}

/** Paragraphs, list items and quotes take continuation lines; table rows do not. */
function opens(line: DocCardLine, raw: string): boolean {
  return line.kind !== "heading" && !TABLE_ROW.test(raw);
}

/**
 * Up to {@link DOC_CARD_MAX_LINES} blocks of a Doc's markdown, as the Doc
 * reads it: a paragraph's soft-wrapped lines join into one block (a hard
 * break stays a line break), a blank line starts the next. A legacy note's
 * text has one block per line. The first `#`/`##` heading is left out: the
 * card's title shows it (`frameDisplayTitle`). Code blocks keep their lines,
 * tables read as `a · b` rows, images and rules are dropped.
 */
export function docCardLines(
  frameType: string,
  source: Record<string, unknown>,
): DocCardLine[] {
  const blocks: DocCardLine[] = [];
  // Legacy note text is not markdown: each of its lines is a paragraph.
  const markdownFlow = !isLegacyNoteContent(frameType, source);
  // The paragraph, list item, quote or code block the next line may continue.
  let open: DocCardLine | null = null;
  let hardBreak = false;
  let titleDropped = false;
  let inFence = false;
  const markdown = docMarkdown(frameType, source).slice(0, SOURCE_MAX_CHARS);
  for (const raw of markdown.split("\n")) {
    if (blocks.length > DOC_CARD_MAX_LINES) break;
    if (FENCE.test(raw)) {
      if (open?.kind === "code") open.text = open.text.trimEnd();
      inFence = !inFence;
      open = null;
      continue;
    }
    if (inFence) {
      const code = raw.trimEnd();
      if (open?.kind === "code") open.text += `\n${code}`;
      else if (code) blocks.push((open = { kind: "code", text: code }));
      continue;
    }
    if (!raw.trim()) {
      open = null;
      continue;
    }
    if (!titleDropped && /^#{1,2}\s+\S/.test(raw)) {
      titleDropped = true;
      open = null;
      continue;
    }
    const line = lineFor(raw);
    if (!line?.text) {
      open = null;
      continue;
    }
    if (markdownFlow && continues(open, line, raw)) {
      Object.assign(open, joinInline([open, line], hardBreak ? "\n" : " "));
    } else {
      blocks.push(line);
      open = opens(line, raw) ? line : null;
    }
    hardBreak = HARD_BREAK.test(raw);
  }
  return blocks.slice(0, DOC_CARD_MAX_LINES);
}

const FIRST_IMAGE = /!\[([^\]]*)\]\(\s*<?([^\s)>]+)>?(?:\s+"[^"]*")?\s*\)/;
const COVER_URL = /^(?:https?:\/\/|\/(?!\/))\S+$/;

/** A Doc's cover: its first image block with a web or app URL, if any. */
export function docCoverImage(
  frameType: string,
  source: Record<string, unknown>,
): { url: string; alt: string } | null {
  const match = FIRST_IMAGE.exec(
    docMarkdown(frameType, source).slice(0, SOURCE_MAX_CHARS),
  );
  const url = match?.[2];
  return match && url && COVER_URL.test(url)
    ? { url, alt: match[1] ?? "" }
    : null;
}
